import React, { useEffect, useState, useCallback } from 'react';
import { Paper, Grid } from '@mui/material';
import { Editor, Frame, Element, useEditor } from "@craftjs/core";
import { useAuth, useQwanta } from 'qwanyx';
import { Toolbox } from './components/Toolbox';
import { SettingsPanel } from './components/SettingsPanel';
import { Topbar } from './components/Topbar';
import { Container } from './components/user/Container';
import { LayersPanel } from './components/LayersPanel';
import { SelectionHighlighter } from './components/SelectionHighlighter';
import { craftResolvers } from './resolvers';
import { getQwantum } from 'qwanyx';

// Component to handle data loading inside Editor context
const EditorDataLoader = ({ cardSavedState, vi, cardData }) => {
 const { actions } = useEditor();
 const [savedState] = useQwanta("card");
 const [loadedVi, setLoadedVi] = useState(null);
 const [loadedCardData, setLoadedCardData] = useState(null);
 const [internalEnabled, setInternalEnabled] = useState(false);

 // Determine which state to use (priority: cardData > cardSavedState > savedState)
 const stateToUse = cardData || cardSavedState || savedState;

 useEffect(() => {
   // Check if we need to load new data
   const hasNewVi = vi && vi !== loadedVi;
   const hasNewCardData = cardData && cardData !== loadedCardData;
   
   if (stateToUse && (hasNewVi || hasNewCardData || (!loadedVi && !loadedCardData))) {
     try {
       actions.deserialize(stateToUse);
       if (vi) setLoadedVi(vi);
       if (cardData) setLoadedCardData(cardData);
     } catch (error) {
       console.error('Failed to load saved state:', error);
     }
   }
 }, [stateToUse, actions, vi, loadedVi, cardData, loadedCardData]);

 return null;
};

// Component to render panels based on editor enabled state
const EditorLayout = ({ vi, cardData, enabled: editorEnabled, compact = false }) => {
 const { enabled } = useEditor((state) => ({
   enabled: state.options.enabled
 }));
 const { logged } = useAuth();

 // FIXED: Use the passed enabled prop instead of just logged && enabled
 // Don't show panels when using cardData (display mode) or vi (template mode) or compact mode
 const showPanels = logged && enabled && editorEnabled && !vi && !cardData && !compact;

 // FIXED: Use compact layout for stack items
 if (compact) {
   return (
     <div style={{ 
       width: '100%', 
       minHeight: 'auto', // Allow natural height
       overflow: 'visible' // Don't clip content
     }}>
       <Frame>
         <Element is={Container} padding={0} canvas>
         </Element>
       </Frame>
     </div>
   );
 }

 return (
   <SelectionHighlighter>
     <div style={{ display: 'flex', height: '100vh' }}>
       {/* Layers Panel - Left - only show if logged in and editor enabled */}
       {showPanels && (
         <div style={{ width: '300px', flexShrink: 0 }}>
           <LayersPanel />
         </div>
       )}

       {/* Main Canvas */}
       <div style={{ flex: 1, overflow: 'auto' }}>
         <Frame>
           <Element is={Container} padding={0} canvas>
           </Element>
         </Frame>
       </div>

       {/* Toolbox & Settings - Right - only show if logged in and editor enabled */}
       {showPanels && (
         <div style={{ width: '320px', flexShrink: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>
           <Paper style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
             <div style={{ zoom: 0.65 }}>
               <Topbar />
             </div>
             <div style={{ zoom: 0.65 }}>
               <Toolbox />
             </div>
             <div style={{ flex: 1, overflow: 'auto' }}>
               <div style={{ zoom: 0.65 }}>
                 <SettingsPanel />
               </div>
             </div>
           </Paper>
         </div>
       )}
     </div>
   </SelectionHighlighter>
 );
};

export const DNDEditor = ({ 
 vi = null,              // Fetch card from database using this ID
 cardData = null,        // Pre-loaded/processed card data (takes priority over vi)
 stackMode = false, 
 enabled = null,
 readOnly = false,
 compact = false         // NEW: Add compact mode prop
}) => {
 const { logged } = useAuth();
 const [cardSavedState, setCardSavedState] = useState(null);
 const [internalEnabled, setInternalEnabled] = useState(null);

 // Calculate effective enabled state
 const baseEnabled = enabled !== null 
   ? enabled 
   : stackMode 
     ? false 
     : logged;
 
 const editorEnabled = internalEnabled;

 const handleKeyDown = useCallback((event) => {
   if (event.altKey && event.key === '0') {
     event.preventDefault();
     setInternalEnabled(prev => !prev);
   }
 }, [baseEnabled]);

 useEffect(() => {
   document.addEventListener('keydown', handleKeyDown);
   return () => document.removeEventListener('keydown', handleKeyDown);
 }, [handleKeyDown]);

 // ENHANCED: Handle both cardData (pre-loaded) and vi (fetch from DB)
 useEffect(() => {
   if (cardData) {
     // Use pre-loaded card data (no database fetch needed)
     setCardSavedState(cardData);
   } else if (vi) {
     // Fetch from database using vi
     const loadData = async () => {
       try {
         const data = await getQwantum(vi, "card");
         setCardSavedState(data);
       } catch (error) {
         console.error('Failed to load card data from vi:', error);
       }
     };
     loadData();
   } else {
     // No data source provided
     setCardSavedState(null);
   }
 }, [vi, cardData]);

 return (
   <div style={{ 
     margin: "0 auto", 
     width: "100%",
     height: compact ? 'auto' : 'auto' // Allow natural height in compact mode
   }}>
     <Editor
       resolver={craftResolvers}
       enabled={editorEnabled}
     >
       <EditorDataLoader 
         cardSavedState={cardSavedState} 
         vi={vi} 
         cardData={cardData}
       />
       <EditorLayout 
         vi={vi} 
         cardData={cardData}
         enabled={editorEnabled}
         compact={compact}  // Pass compact mode
       />
     </Editor>
   </div>
 );
};